<template>
  <div
    class="flex items-center justify-center min-h-screen px-4 py-12 bg-white dark:bg-zinc-950"
  >
    <div
      class="flex flex-col items-center justify-between w-full gap-8 space-y-8 lg:flex-row max-w-7xl lg:space-y-0 lg:space-x-16"
    >
      <!-- Carousel -->

      <CustomCarousel :isLoading="loading" :items="items" />

      <!-- Text + Accordion -->
      <div class="max-w-lg space-y-6">
        <!-- Title and Description -->
        <h2
          class="text-3xl font-bold text-center text-gray-800 dark:text-zinc-100 lg:text-left"
        >
          {{ $t("studio.studioTitle") }}
        </h2>
        <p
          class="m-4 text-lg leading-relaxed text-center text-gray-600 dark:text-zinc-400 lg:m-0 lg:text-left"
        >
          {{ $t("studio.studioDescription") }}
        </p>

        <!-- Loading Skeletons for Accordion -->
        <div v-if="loading" class="space-y-4">
          <Skeleton class="w-full h-14" />
        </div>

        <!-- Error message -->
        <div v-else-if="error" class="text-red-500">{{ error }}</div>

        <!-- Accordion content -->
        <div v-else>
          <Accordion type="single" collapsible class="w-full">
            <AccordionItem
              v-if="studioMaterial?.key"
              :value="studioMaterial.key"
            >
              <AccordionTrigger
                class="text-lg text-gray-900 no-underline uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700 focus:outline-none dark:text-zinc-100"
              >
                {{
                  studioMaterial?.titleKey
                    ? $t(studioMaterial.titleKey)
                    : "Loading..."
                }}
              </AccordionTrigger>
              <AccordionContent
                class="text-base text-gray-700 dark:text-zinc-300"
              >
                <Accordion type="multiple" collapsible>
                  <AccordionItem
                    v-for="(group, gIndex) in studioMaterial?.items || []"
                    :key="gIndex"
                    :value="group?.groupKey || gIndex"
                  >
                    <AccordionTrigger
                      class="pl-4 text-base font-medium text-left text-gray-900 cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700 dark:text-zinc-100"
                    >
                      {{ group?.emoji }}
                      {{ group?.groupKey ? $t(group.groupKey) : "Loading..." }}
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul
                        class="pl-6 pr-2 space-y-2 text-gray-800 list-disc list-inside dark:text-zinc-100"
                      >
                        <li
                          v-for="(item, index) in group?.items || []"
                          :key="index"
                        >
                          <span
                            class="font-semibold text-black dark:text-zinc-100"
                            >{{ item?.name || "Item" }}</span
                          >
                        </li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <!-- Contact Button -->
        <ContactButtons />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const items = ref<any[]>([]);

// Fetch materials and images
const {
  materials: studioMaterial,
  loading: materialsLoading,
  error: materialsError,
} = useMaterials();

const {
  images: studioImages,
  loading: imagesLoading,
  error: imagesError,
} = useStudioImages();

// Keep items reactive when studioImages changes
watch(studioImages, (newImages) => {
  items.value = newImages || [];
});

// Combine loading and error states
const loading = ref(true);
watch([imagesLoading, materialsLoading], () => {
  loading.value = imagesLoading.value || materialsLoading.value;
});

const error = ref<string | null>(null);
watch([materialsError, imagesError], () => {
  error.value = materialsError.value || imagesError.value || null;
});

// i18n
const i18n = useI18n();

// SEO metadata
useSeoMeta({
  title: i18n.t("menuKeys.studio"),
  description:
    "Explore the Highfield Music Studio, equipped with top-of-the-line audio equipment, instruments, and professional recording services.",
  keywords:
    "studio, music, instruments, recording, audio equipment, midi, dj gear, microphones, studio equipment",
  ogTitle: "Studio | Highfield Music Studio",
  ogDescription:
    "Explore the Highfield Music Studio, equipped with top-of-the-line audio equipment, instruments, and professional recording services.",
  ogImage: "/images/logos/studio/1.jpg",
  twitterCard: "summary_large_image",
  twitterTitle: "Studio | Highfield Music Studio",
  twitterDescription:
    "Explore the Highfield Music Studio, equipped with top-of-the-line audio equipment, instruments, and professional recording services.",
  twitterImage: "/images/media/studio/1.jpg",
});
</script>
